// Advanced utility functions for fractal and wave pattern analysis.

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { Node, Edge, VoiceCharacteristics, LinguisticElement } from './types';

// Enhanced fractal dimension calculation
export function getFractalDimension(network: NeuralNetworkSimulator): number {
	const branchLengths = network.branches.map(b => b.length);
	const avgLength = branchLengths.length > 0 ? branchLengths.reduce((a, b) => a + b, 0) / branchLengths.length : 1;
	const baseDimension = Math.log(network.nodes.length) / Math.log(avgLength || 1.1);
	
	// Adjust based on network complexity and connectivity
	const connectivity = network.edges.length / Math.max(1, network.nodes.length);
	const complexity = network.branches.length / Math.max(1, network.nodes.length);
	
	return baseDimension * (1 + connectivity * 0.3) * (1 + complexity * 0.2);
}

// Advanced wave pattern signature with energy flow
export function getWavePatternSignature(network: NeuralNetworkSimulator): number[] {
	const signature: number[] = [];
	
	// Base wave patterns
	signature.push(...network.wavePatterns.slice(0, 10));
	
	// Energy flow patterns
	const energyPatterns = network.nodes.map(node => node.energy || 0);
	signature.push(...energyPatterns.slice(0, 10));
	
	// Connectivity patterns
	const connectivityPatterns = network.nodes.map(node => {
		const connections = network.edges.filter(e => e.from === node.id || e.to === node.id).length;
		return connections / Math.max(1, network.nodes.length);
	});
	signature.push(...connectivityPatterns.slice(0, 10));
	
	return signature;
}

// Generate fractal growth patterns
export function generateFractalGrowthPattern(
	centerX: number, 
	centerY: number, 
	scale: number, 
	complexity: number
): { x: number; y: number; angle: number; energy: number }[] {
	const pattern: { x: number; y: number; angle: number; energy: number }[] = [];
	const goldenRatio = 1.618;
	const maxBranches = Math.floor(complexity * 8) + 3;
	
	for (let i = 0; i < maxBranches; i++) {
		const angle = (i * 2 * Math.PI / maxBranches) + (Math.random() - 0.5) * 0.5;
		const distance = scale * Math.pow(goldenRatio, i % 5) * (0.8 + Math.random() * 0.4);
		const energy = 0.5 + Math.sin(i * goldenRatio) * 0.3;
		
		pattern.push({
			x: centerX + Math.cos(angle) * distance,
			y: centerY + Math.sin(angle) * distance,
			angle: angle,
			energy: energy
		});
	}
	
	return pattern;
}

// Calculate network resonance patterns
export function calculateNetworkResonance(network: NeuralNetworkSimulator): number[] {
	const resonance: number[] = [];
	const nodes = network.nodes;
	const edges = network.edges;
	
	// Calculate resonance for each node based on its connections and energy
	for (const node of nodes) {
		const connectedEdges = edges.filter(e => e.from === node.id || e.to === node.id);
		const totalWeight = connectedEdges.reduce((sum, e) => sum + e.weight, 0);
		const avgEnergy = connectedEdges.length > 0 ? 
			connectedEdges.reduce((sum, e) => {
				const otherNode = nodes.find(n => n.id === (e.from === node.id ? e.to : e.from));
				return sum + (otherNode?.energy || 0);
			}, 0) / connectedEdges.length : 0;
		
		const nodeResonance = (node.energy || 0) * totalWeight * (1 + avgEnergy);
		resonance.push(nodeResonance);
	}
	
	return resonance;
}

// Generate living fractal patterns based on network state
export function generateLivingFractalPattern(network: NeuralNetworkSimulator): {
	patterns: { x: number; y: number; intensity: number; color: string }[];
	lifeForce: number;
	complexity: number;
} {
	const patterns: { x: number; y: number; intensity: number; color: string }[] = [];
	const time = network.step / 60;
	
	// Calculate life force from crystal heart
	let lifeForce = 0.5;
	if (network.crystalHeartId !== null) {
		const heart = network.nodes[network.crystalHeartId];
		if (heart) {
			lifeForce = (heart.energy || 0) / 10; // Normalize heart energy
		}
	}
	
	// Generate patterns based on network state
	const complexity = Math.min(1.0, network.nodes.length / 100);
	const resonance = calculateNetworkResonance(network);
	
	for (let i = 0; i < network.nodes.length; i++) {
		const node = network.nodes[i];
		const nodeResonance = resonance[i] || 0;
		
		// Create fractal patterns around each node
		const patternCount = Math.floor(nodeResonance * 3) + 1;
		for (let p = 0; p < patternCount; p++) {
			const angle = (p * 2 * Math.PI / patternCount) + time * 0.1;
			const distance = 20 + Math.sin(time + i) * 10;
			const intensity = (nodeResonance / 10) * lifeForce * (0.5 + Math.sin(time * 2 + i) * 0.3);
			
			patterns.push({
				x: node.x + Math.cos(angle) * distance,
				y: node.y + Math.sin(angle) * distance,
				intensity: Math.max(0, intensity),
				color: `hsl(${(node.value * 360 + time * 10) % 360}, 70%, ${50 + intensity * 30}%)`
			});
		}
	}
	
	return {
		patterns,
		lifeForce,
		complexity
	};
}

// Analyze network evolution patterns
export function analyzeNetworkEvolution(network: NeuralNetworkSimulator): {
	fractalDimension: number;
	waveSignature: number[];
	resonance: number[];
	evolutionPhase: 'birth' | 'growth' | 'maturity' | 'transformation';
} {
	const fractalDimension = getFractalDimension(network);
	const waveSignature = getWavePatternSignature(network);
	const resonance = calculateNetworkResonance(network);
	
	// Determine evolution phase based on network characteristics
	let evolutionPhase: 'birth' | 'growth' | 'maturity' | 'transformation';
	if (network.nodes.length < 20) {
		evolutionPhase = 'birth';
	} else if (network.nodes.length < 100) {
		evolutionPhase = 'growth';
	} else if (network.nodes.length < 500) {
		evolutionPhase = 'maturity';
	} else {
		evolutionPhase = 'transformation';
	}
	
	return {
		fractalDimension,
		waveSignature,
		resonance,
		evolutionPhase
	};
}

// Enhanced fractal voice characterization using advanced mathematical models
export function generateFractalVoiceCharacteristics(
	network: NeuralNetworkSimulator,
	baseCharacteristics: VoiceCharacteristics
): VoiceCharacteristics {
	const time = network.step / 60;
	const fractalDimension = getFractalDimension(network);
	const resonance = calculateNetworkResonance(network);
	const avgResonance = resonance.reduce((a, b) => a + b, 0) / resonance.length;
	
	// Mandelbrot-inspired voice modulation
	const mandelbrotModulation = (x: number, y: number, iterations: number = 50): number => {
		let zx = 0, zy = 0;
		for (let i = 0; i < iterations; i++) {
			const zxNew = zx * zx - zy * zy + x;
			const zyNew = 2 * zx * zy + y;
			if (zxNew * zxNew + zyNew * zyNew > 4) return i / iterations;
			zx = zxNew;
			zy = zyNew;
		}
		return 1;
	};
	
	// Julia set-inspired formant variation
	const juliaModulation = (x: number, y: number, cx: number, cy: number): number => {
		let zx = x, zy = y;
		for (let i = 0; i < 30; i++) {
			const zxNew = zx * zx - zy * zy + cx;
			const zyNew = 2 * zx * zy + cy;
			if (zxNew * zxNew + zyNew * zyNew > 4) return i / 30;
			zx = zxNew;
			zy = zyNew;
		}
		return 1;
	};
	
	// Generate fractal-based voice parameters
	const fractalPitch = baseCharacteristics.pitch * (1 + Math.sin(time * 0.1) * 0.1 * fractalDimension);
	const fractalPitchVariation = baseCharacteristics.pitchVariation * (1 + avgResonance * 0.3);
	
	// Apply Mandelbrot modulation to formants
	const mandelbrotX = Math.sin(time * 0.05) * 0.5;
	const mandelbrotY = Math.cos(time * 0.07) * 0.5;
	const mandelbrotFactor = mandelbrotModulation(mandelbrotX, mandelbrotY);
	
	// Apply Julia set modulation to formants
	const juliaCX = Math.sin(time * 0.03) * 0.3;
	const juliaCY = Math.cos(time * 0.04) * 0.3;
	const juliaFactor = juliaModulation(mandelbrotX, mandelbrotY, juliaCX, juliaCY);
	
	// Fractal formant modulation
	const formantModulation = (mandelbrotFactor + juliaFactor) / 2;
	const fractalFormant1 = baseCharacteristics.formant1 * (1 + formantModulation * 0.2);
	const fractalFormant2 = baseCharacteristics.formant2 * (1 + formantModulation * 0.15);
	const fractalFormant3 = baseCharacteristics.formant3 * (1 + formantModulation * 0.1);
	
	// Golden ratio-based temporal characteristics
	const goldenRatio = 1.618;
	const goldenModulation = Math.sin(time * goldenRatio) * 0.1;
	
	// Fractal envelope characteristics
	const fractalAttack = baseCharacteristics.attackTime * (1 + goldenModulation * 0.3);
	const fractalDecay = baseCharacteristics.decayTime * (1 + Math.cos(time * 0.08) * 0.2);
	const fractalSustain = baseCharacteristics.sustainLevel * (1 + avgResonance * 0.1);
	const fractalRelease = baseCharacteristics.releaseTime * (1 + Math.sin(time * 0.12) * 0.25);
	
	// Fractal breathiness and roughness
	const fractalBreathiness = baseCharacteristics.breathiness * (1 + fractalDimension * 0.2);
	const fractalRoughness = baseCharacteristics.roughness * (1 + Math.sin(time * 0.15) * 0.3);
	
	// Fractal speech patterns
	const fractalSpeechRate = baseCharacteristics.speechRate * (1 + Math.cos(time * 0.09) * 0.15);
	const fractalPauseFreq = baseCharacteristics.pauseFrequency * (1 + Math.sin(time * 0.11) * 0.2);
	const fractalPauseDur = baseCharacteristics.pauseDuration * (1 + Math.cos(time * 0.13) * 0.25);
	const fractalEmphasis = baseCharacteristics.emphasisStrength * (1 + avgResonance * 0.2);
	
	// Fractal intonation patterns
	const fractalIntonationRange = baseCharacteristics.intonationRange * (1 + fractalDimension * 0.15);
	const fractalQuestionRise = baseCharacteristics.questionRise * (1 + Math.sin(time * 0.14) * 0.2);
	const fractalStatementFall = baseCharacteristics.statementFall * (1 + Math.cos(time * 0.16) * 0.2);
	const fractalExclamationRise = baseCharacteristics.exclamationRise * (1 + Math.sin(time * 0.18) * 0.25);
	
	return {
		...baseCharacteristics,
		pitch: Math.max(50, Math.min(800, fractalPitch)),
		pitchVariation: Math.max(0.1, Math.min(2.0, fractalPitchVariation)),
		formant1: Math.max(200, Math.min(1200, fractalFormant1)),
		formant2: Math.max(800, Math.min(3000, fractalFormant2)),
		formant3: Math.max(1500, Math.min(4000, fractalFormant3)),
		attackTime: Math.max(10, Math.min(200, fractalAttack)),
		decayTime: Math.max(50, Math.min(500, fractalDecay)),
		sustainLevel: Math.max(0.1, Math.min(1.0, fractalSustain)),
		releaseTime: Math.max(50, Math.min(1000, fractalRelease)),
		breathiness: Math.max(0, Math.min(1, fractalBreathiness)),
		roughness: Math.max(0, Math.min(1, fractalRoughness)),
		speechRate: Math.max(0.5, Math.min(2.0, fractalSpeechRate)),
		pauseFrequency: Math.max(0, Math.min(1, fractalPauseFreq)),
		pauseDuration: Math.max(100, Math.min(2000, fractalPauseDur)),
		emphasisStrength: Math.max(0, Math.min(1, fractalEmphasis)),
		intonationRange: Math.max(50, Math.min(500, fractalIntonationRange)),
		questionRise: Math.max(0, Math.min(1, fractalQuestionRise)),
		statementFall: Math.max(0, Math.min(1, fractalStatementFall)),
		exclamationRise: Math.max(0, Math.min(1, fractalExclamationRise))
	};
}

// Advanced fractal formant calculation using L-systems and chaos theory
export function calculateFractalFormants(
	element: LinguisticElement,
	network: NeuralNetworkSimulator,
	baseFormants: { f1: number, f2: number, f3: number }
): { f1: number, f2: number, f3: number, f4?: number, f5?: number } {
	const time = network.step / 60;
	const fractalDimension = getFractalDimension(network);
	const resonance = calculateNetworkResonance(network);
	const avgResonance = resonance.reduce((a, b) => a + b, 0) / resonance.length;
	
	// L-system inspired formant evolution
	const lSystemIteration = (axiom: string, rules: { [key: string]: string }, iterations: number): string => {
		let result = axiom;
		for (let i = 0; i < iterations; i++) {
			let newResult = '';
			for (const char of result) {
				newResult += rules[char] || char;
			}
			result = newResult;
		}
		return result;
	};
	
	// Define L-system rules for formant evolution
	const formantRules = {
		'F': 'F[+F]F[-F]F',
		'[': '[',
		']': ']',
		'+': '+',
		'-': '-'
	};
	
	// Generate formant pattern based on phonetic complexity
	const complexity = element.complexity || 1;
	const formantPattern = lSystemIteration('F', formantRules, Math.floor(complexity * 2));
	const patternLength = formantPattern.length;
	
	// Chaos theory inspired formant modulation
	const logisticMap = (x: number, r: number): number => r * x * (1 - x);
	const chaosValue = (() => {
		let x = 0.5;
		for (let i = 0; i < 20; i++) {
			x = logisticMap(x, 3.7 + Math.sin(time * 0.1) * 0.2);
		}
		return x;
	})();
	
	// Fractal scaling factors
	const fractalScale = 1 + (fractalDimension - 1) * 0.3;
	const resonanceScale = 1 + avgResonance * 0.2;
	const chaosScale = 1 + (chaosValue - 0.5) * 0.4;
	const patternScale = 1 + (patternLength / 100) * 0.1;
	
	// Apply fractal transformations to formants
	const f1 = baseFormants.f1 * fractalScale * resonanceScale * chaosScale * patternScale;
	const f2 = baseFormants.f2 * fractalScale * resonanceScale * chaosScale * patternScale;
	const f3 = baseFormants.f3 * fractalScale * resonanceScale * chaosScale * patternScale;
	
	// Add higher formants for more realistic voice
	const f4 = f3 * 1.3 * (1 + Math.sin(time * 0.2) * 0.1);
	const f5 = f4 * 1.2 * (1 + Math.cos(time * 0.25) * 0.1);
	
	// Apply vowel-specific fractal adjustments
	const vowelAdjustment = getVowelFractalAdjustment(element, time, fractalDimension);
	
	return {
		f1: Math.max(200, Math.min(1200, f1 * vowelAdjustment.f1)),
		f2: Math.max(800, Math.min(3000, f2 * vowelAdjustment.f2)),
		f3: Math.max(1500, Math.min(4000, f3 * vowelAdjustment.f3)),
		f4: Math.max(2000, Math.min(5000, f4 * vowelAdjustment.f4)),
		f5: Math.max(2500, Math.min(6000, f5 * vowelAdjustment.f5))
	};
}

// Vowel-specific fractal adjustments
export function getVowelFractalAdjustment(
	element: LinguisticElement,
	time: number,
	fractalDimension: number
): { f1: number, f2: number, f3: number, f4: number, f5: number } {
	const phoneticValue = element.phoneticValue.toLowerCase();
	
	// Base adjustments for different vowel types
	let baseAdjustment = { f1: 1, f2: 1, f3: 1, f4: 1, f5: 1 };
	
	// Front vowels - higher F2, lower F1
	if (['i', 'ɪ', 'iː', 'e', 'ɛ', 'eː', 'æ', 'y', 'ʏ', 'ø', 'œ'].includes(phoneticValue)) {
		baseAdjustment = {
			f1: 0.8 + Math.sin(time * 0.1) * 0.1,
			f2: 1.2 + Math.cos(time * 0.15) * 0.15,
			f3: 1.1 + Math.sin(time * 0.2) * 0.1,
			f4: 1.05 + Math.cos(time * 0.25) * 0.05,
			f5: 1.02 + Math.sin(time * 0.3) * 0.03
		};
	}
	// Back vowels - lower F2, higher F1
	else if (['u', 'ʊ', 'uː', 'o', 'ɔ', 'oː', 'ɑ', 'ʌ', 'ɐ'].includes(phoneticValue)) {
		baseAdjustment = {
			f1: 1.1 + Math.cos(time * 0.12) * 0.1,
			f2: 0.8 + Math.sin(time * 0.18) * 0.1,
			f3: 0.9 + Math.cos(time * 0.22) * 0.1,
			f4: 0.95 + Math.sin(time * 0.28) * 0.05,
			f5: 0.98 + Math.cos(time * 0.35) * 0.02
		};
	}
	// Central vowels - balanced
	else if (['ə', 'ɜ', 'ɜː', 'ɨ', 'ɯ'].includes(phoneticValue)) {
		baseAdjustment = {
			f1: 1.0 + Math.sin(time * 0.14) * 0.05,
			f2: 1.0 + Math.cos(time * 0.16) * 0.05,
			f3: 1.0 + Math.sin(time * 0.18) * 0.05,
			f4: 1.0 + Math.cos(time * 0.2) * 0.03,
			f5: 1.0 + Math.sin(time * 0.25) * 0.02
		};
	}
	
	// Apply fractal dimension scaling
	const fractalScale = 1 + (fractalDimension - 1) * 0.1;
	
	return {
		f1: baseAdjustment.f1 * fractalScale,
		f2: baseAdjustment.f2 * fractalScale,
		f3: baseAdjustment.f3 * fractalScale,
		f4: baseAdjustment.f4 * fractalScale,
		f5: baseAdjustment.f5 * fractalScale
	};
}

// Advanced fractal pitch modulation using attractor dynamics
export function calculateFractalPitchModulation(
	basePitch: number,
	network: NeuralNetworkSimulator,
	element: LinguisticElement
): number {
	const time = network.step / 60;
	const fractalDimension = getFractalDimension(network);
	const resonance = calculateNetworkResonance(network);
	const avgResonance = resonance.reduce((a, b) => a + b, 0) / resonance.length;
	
	// Lorenz attractor for pitch variation
	const lorenzAttractor = (x: number, y: number, z: number, dt: number = 0.01) => {
		const sigma = 10, rho = 28, beta = 8/3;
		const dx = sigma * (y - x) * dt;
		const dy = (x * (rho - z) - y) * dt;
		const dz = (x * y - beta * z) * dt;
		return { x: x + dx, y: y + dy, z: z + dz };
	};
	
	// Initialize attractor state based on element properties
	let lorenzState = {
		x: (element.frequency || 100) / 1000,
		y: avgResonance * 0.1,
		z: fractalDimension * 0.1
	};
	
	// Evolve attractor for several steps
	for (let i = 0; i < 10; i++) {
		lorenzState = lorenzAttractor(lorenzState.x, lorenzState.y, lorenzState.z);
	}
	
	// Use attractor state for pitch modulation
	const attractorModulation = Math.sin(lorenzState.x * 10) * 0.1 + 
								 Math.cos(lorenzState.y * 10) * 0.05 + 
								 Math.sin(lorenzState.z * 10) * 0.03;
	
	// Add fractal-based micro-variations
	const fractalMicroVariation = Math.sin(time * fractalDimension * 0.5) * 0.02;
	
	// Add resonance-based pitch variation
	const resonanceVariation = Math.cos(time * avgResonance * 0.3) * 0.05;
	
	// Combine all modulations
	const totalModulation = 1 + attractorModulation + fractalMicroVariation + resonanceVariation;
	
	return Math.max(50, Math.min(800, basePitch * totalModulation));
}

// Fractal-based voice texture generation
export function generateFractalVoiceTexture(
	network: NeuralNetworkSimulator,
	baseTexture: number
): number {
	const time = network.step / 60;
	const fractalDimension = getFractalDimension(network);
	const resonance = calculateNetworkResonance(network);
	const avgResonance = resonance.reduce((a, b) => a + b, 0) / resonance.length;
	
	// Generate fractal noise using midpoint displacement
	const fractalNoise = (x: number, y: number, octaves: number = 4): number => {
		let value = 0;
		let amplitude = 1;
		let frequency = 1;
		let maxValue = 0;
		
		for (let i = 0; i < octaves; i++) {
			value += Math.sin(x * frequency) * Math.cos(y * frequency) * amplitude;
			maxValue += amplitude;
			amplitude *= 0.5;
			frequency *= 2;
		}
		
		return value / maxValue;
	};
	
	// Generate texture based on fractal noise
	const noiseX = time * 0.1;
	const noiseY = fractalDimension * 0.5;
	const fractalNoiseValue = fractalNoise(noiseX, noiseY, Math.floor(fractalDimension * 3) + 1);
	
	// Apply resonance-based texture variation
	const resonanceTexture = Math.sin(time * avgResonance * 0.2) * 0.3;
	
	// Combine with base texture
	const totalTexture = baseTexture * (1 + fractalNoiseValue * 0.4 + resonanceTexture);
	
	return Math.max(0, Math.min(1, totalTexture));
}
